import React from 'react';
import { Box, Button, Chip, Grid, Link, Typography } from '@mui/material';
import {
    EditOutlined,
    LocationOnOutlined,
    PictureAsPdf,
    PublicOutlined,
    ScheduleOutlined,
    Visibility,
} from '@mui/icons-material';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { IAudition } from '../../../../types/global/helper';
import { RichTextViewer } from '../../../Common/RichTextViewer';
import { getAuditionSynopsisLabel } from '../../../../utils/auditionRichTextCopy';
import { CompanyAvatar } from '../../../Avatar/CompanyAvatar';
import {
    BRAND_LUXE_GOLD,
    BRAND_LUXE_PRIMARY,
    BRAND_OUTLINE_VARIANT,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../../../../pages/BrandDashboard/brandDashboardTokens';

export interface AuditionDetailsTabProps {
    audition?: IAudition;
    onOpenDetailsEdit: () => void;
}

export const AuditionDetailsTab: React.FC<AuditionDetailsTabProps> = ({ audition, onOpenDetailsEdit }) => {
    if (!audition) {
        return (
            <Typography
                sx={{
                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                    fontSize: '1rem',
                    color: 'rgba(255,255,255,0.7)',
                }}>
                No audition details available
            </Typography>
        );
    }

    const formatDate = (value?: string | Date) => (value ? new Date(value).toLocaleDateString() : 'Not specified');
    const titleCase = (value: string) =>
        value
            .split(/[\s_-]+/)
            .filter(Boolean)
            .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ');

    const synopsisLabel = getAuditionSynopsisLabel(audition.gig_type);
    const richTextViewSx = {
        padding: 0,
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontSize: 14,
        lineHeight: 1.65,
    } as const;
    const hideMoney = (audition as { access?: { can_view_prices?: boolean } })?.access?.can_view_prices === false;
    const rewardRange = hideMoney
        ? 'xxxxx'
        : audition.min_reward && audition.max_reward
        ? `${audition.currency || 'USD'} ${Number(audition.min_reward).toLocaleString()} - ${Number(
              audition.max_reward,
          ).toLocaleString()}`
        : audition.reward
        ? `${audition.currency || 'USD'} ${Number(audition.reward).toLocaleString()}`
        : 'Not specified';

    const sectionTitleSx = {
        fontFamily: FONT_ENUM.MAIN_BOLD,
        color: BRAND_LUXE_PRIMARY,
        fontSize: '1.25rem',
        textTransform: 'uppercase' as const,
        mb: 2,
    };

    const labelSx = {
        fontFamily: FONT_ENUM.MAIN_BOLD,
        fontSize: '0.82rem',
        color: 'rgba(255,255,255,0.38)',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.02em',
    };

    const valueSx = {
        fontFamily: FONT_ENUM.MAIN_MEDIUM,
        fontSize: '1.02rem',
        color: 'rgba(255,255,255,0.92)',
        mt: 0.6,
    };

    const cardSx = {
        bgcolor: BRAND_SURFACE_CONTAINER_LOW,
        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
        borderRadius: 2.5,
        p: 3,
    };

    return (
        <Box sx={{ py: 2, width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                    startIcon={<EditOutlined />}
                    onClick={onOpenDetailsEdit}
                    sx={{
                        px: 3,
                        py: 1.25,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 13,
                        bgcolor: BRAND_LUXE_GOLD,
                        border: `1px solid ${BRAND_LUXE_GOLD}`,
                        color: '#131517',
                        '&:hover': { bgcolor: BRAND_LUXE_GOLD },
                        '& .MuiButton-startIcon': { color: '#131517' },
                    }}>
                    Edit
                </Button>
            </Box>

            <Grid container spacing={2.5}>
                <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
                    <Box sx={{ ...cardSx, width: '100%', height: '100%' }}>
                        <Typography sx={sectionTitleSx}>Casting Details</Typography>
                        <Typography sx={labelSx}>Title</Typography>
                        <Typography sx={{ ...valueSx, mb: 2 }}>{audition.title || 'Untitled audition'}</Typography>

                        <Typography sx={labelSx}>Type of Work</Typography>
                        <Typography sx={{ ...valueSx, mb: 2 }}>{audition.type_of_work || 'Short'}</Typography>

                        <Typography sx={labelSx}>{synopsisLabel}</Typography>
                        <Box sx={{ mt: 0.8, color: 'rgba(255,255,255,0.88)', mb: 2 }}>
                            <RichTextViewer
                                content={audition.synopsis || 'No synopsis provided'}
                                textStyle={richTextViewSx}
                            />
                        </Box>

                        <Typography sx={labelSx}>Plot Summary</Typography>
                        <Box sx={{ mt: 0.8, color: 'rgba(255,255,255,0.8)' }}>
                            <RichTextViewer content={audition.plot || 'No plot provided'} textStyle={richTextViewSx} />
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                    <Box sx={{ ...cardSx, width: '100%', height: '100%' }}>
                        <Typography sx={sectionTitleSx}>Compensation & Employment</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <Typography sx={labelSx}>Reward Range</Typography>
                                <Typography
                                    sx={{
                                        ...valueSx,
                                        color: BRAND_LUXE_PRIMARY,
                                        filter: hideMoney ? 'blur(6px)' : 'none',
                                        userSelect: hideMoney ? 'none' : 'auto',
                                    }}>
                                    {rewardRange}
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={labelSx}>Frequency</Typography>
                                <Typography sx={valueSx}>{audition.reward_frequency || 'Per gig'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={labelSx}>Type</Typography>
                                <Typography sx={valueSx}>{audition.compensation || 'Unpaid'}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <Typography sx={labelSx}>Employment Type</Typography>
                                <Typography sx={valueSx}>{audition.employment_type || 'One-off contract'}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Required Media</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {(audition.required_media || []).length > 0 ? (
                                audition.required_media?.map((media, index) => (
                                    <Typography key={`${media}-${index}`} sx={valueSx}>
                                        - {titleCase(String(media))}
                                    </Typography>
                                ))
                            ) : (
                                <Typography sx={valueSx}>None specified</Typography>
                            )}
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Required Skills</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
                            {(audition.required_skill || []).length > 0 ? (
                                audition.required_skill?.map((skill, index) => (
                                    <Chip
                                        key={`${skill}-${index}`}
                                        label={titleCase(String(skill))}
                                        sx={{
                                            borderRadius: 1,
                                            height: 28,
                                            bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                            border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                            color: BRAND_LUXE_PRIMARY,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}
                                    />
                                ))
                            ) : (
                                <Typography sx={valueSx}>None specified</Typography>
                            )}
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Age Range</Typography>
                        <Typography sx={valueSx}>
                            {audition.min_age && audition.max_age
                                ? `${audition.min_age}-${audition.max_age} years`
                                : 'Not specified'}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Production</Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography sx={labelSx}>Production Company</Typography>
                                {audition.production_company ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
                                        {audition.production_company._id && (
                                            <CompanyAvatar company={audition.production_company} size="small" />
                                        )}
                                        <Box>
                                            <Typography sx={valueSx}>
                                                {audition.production_company.company_name || 'Unnamed company'}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: '0.86rem',
                                                    color: 'rgba(255,255,255,0.45)',
                                                }}>
                                                {audition.production_company.company_type || 'Production Company'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography sx={valueSx}>No production company assigned</Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography sx={labelSx}>Production Personnel</Typography>
                                <Typography sx={valueSx}>
                                    {audition.production_personel || 'No personnel information provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography sx={labelSx}>Company Website</Typography>
                                <Typography sx={valueSx}>
                                    {audition.company_website || 'No website provided'}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography sx={labelSx}>Tags</Typography>
                                {(audition.tags || []).length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.8 }}>
                                        {audition.tags?.map((tag, index) => (
                                            <Chip
                                                key={`${tag}-${index}`}
                                                label={titleCase(String(tag))}
                                                sx={{
                                                    borderRadius: 1,
                                                    height: 28,
                                                    bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                                    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                                    color: BRAND_LUXE_PRIMARY,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                }}
                                            />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={valueSx}>No tags assigned</Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography sx={labelSx}>Union Affiliation</Typography>
                                <Typography sx={valueSx}>{audition.union_affiliation || 'not applicable'}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Timeline</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1.2 }}>
                                <ScheduleOutlined sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18, mt: 0.4 }} />
                                <Box>
                                    <Typography sx={labelSx}>Job Start Date</Typography>
                                    <Typography sx={valueSx}>{formatDate(audition.job_start_date)}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.2 }}>
                                <ScheduleOutlined sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18, mt: 0.4 }} />
                                <Box>
                                    <Typography sx={labelSx}>Job End Date</Typography>
                                    <Typography sx={valueSx}>{formatDate(audition.job_end_date)}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.2 }}>
                                <ScheduleOutlined sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18, mt: 0.4 }} />
                                <Box>
                                    <Typography sx={labelSx}>Rehearsal Date</Typography>
                                    <Typography sx={valueSx}>{formatDate(audition.rehearsal_date)}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.2 }}>
                                <ScheduleOutlined sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18, mt: 0.4 }} />
                                <Box>
                                    <Typography sx={labelSx}>Casting Expiration</Typography>
                                    <Typography sx={valueSx}>{formatDate(audition.casting_expiration)}</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Location Details</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 1.2 }}>
                                <PublicOutlined sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18, mt: 0.4 }} />
                                <Box>
                                    <Typography sx={labelSx}>Shoot Location</Typography>
                                    <Typography sx={valueSx}>
                                        {audition.shoot_location?.address || 'Worldwide'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.2 }}>
                                <LocationOnOutlined sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18, mt: 0.4 }} />
                                <Box>
                                    <Typography sx={labelSx}>Work Location</Typography>
                                    <Typography sx={valueSx}>
                                        {Array.isArray(audition.work_location) && audition.work_location.length > 0
                                            ? audition.work_location
                                                  .map(loc => loc.address)
                                                  .filter(Boolean)
                                                  .join(', ')
                                            : audition.work_location?.address || 'Not specified'}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.2 }}>
                                <LocationOnOutlined sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18, mt: 0.4 }} />
                                <Box>
                                    <Typography sx={labelSx}>Talent Location</Typography>
                                    <Typography sx={valueSx}>
                                        {Array.isArray(audition.talent_location) && audition.talent_location.length > 0
                                            ? audition.talent_location
                                                  .map(loc => loc.address)
                                                  .filter(Boolean)
                                                  .join(', ')
                                            : audition.talent_location?.address || 'Not specified'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Attachments</Typography>
                        {audition.attachment ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box
                                    sx={{
                                        p: 1.2,
                                        borderRadius: 1,
                                        bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST,
                                        border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                    }}>
                                    <PictureAsPdf sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 18 }} />
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: 'rgba(255,255,255,0.88)',
                                            fontSize: '0.9rem',
                                            flex: 1,
                                        }}>
                                        {audition.attachment_description || 'Attachment'}
                                    </Typography>
                                    <Link
                                        href={audition.attachment}
                                        target="_blank"
                                        rel="noreferrer"
                                        sx={{ color: BRAND_LUXE_PRIMARY, display: 'inline-flex' }}>
                                        <Visibility sx={{ fontSize: 18 }} />
                                    </Link>
                                </Box>
                                <Typography sx={{ ...labelSx, mt: 0.5 }}>
                                    {audition.attachment_type || 'No type specified'}
                                </Typography>
                            </Box>
                        ) : (
                            <Typography sx={valueSx}>No attachment added</Typography>
                        )}
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Report Frequency</Typography>
                        <Typography sx={valueSx}>
                            {audition.submission_report || 'Applications as they come in'}
                        </Typography>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Box sx={cardSx}>
                        <Typography sx={sectionTitleSx}>Usage</Typography>
                        <Typography sx={valueSx}>
                            {(audition.usage || []).length > 0
                                ? (audition.usage || []).join(', ')
                                : 'No usage specified'}
                        </Typography>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};
